import type { Script } from './domain.js';

/**
 * The words a line is spoken with: stage directions in brackets ("[Pause]", "[Blick halten]") are for the reader of
 * the teleprompter, never spoken. Falls back to the text itself when nothing else is left.
 */
export function spokenText(text: string): string {
  const s = text
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s || text;
}

/** True when a line has something to say: at least one word of two or more letters or digits outside brackets. */
export function isSpeakable(text: string): boolean {
  const s = text.replace(/\[[^\]]*\]/g, ' ');
  return /[\p{L}\p{N}]{2,}/u.test(s);
}

/**
 * 1-based numbers of the lines without spoken words. Such a line can never be found in a take, so the video fails
 * with "line N is missing": approving, recording and rendering are refused until it is written or removed.
 */
export function silentLines(script: Pick<Script, 'lines'>): number[] {
  return script.lines.flatMap((l, i) => (isSpeakable(l.text ?? '') ? [] : [i + 1]));
}

export function silentLinesMessage(lines: number[]): string {
  return lines.length === 1
    ? `Line ${lines[0]} has no spoken text. Write it or remove the line.`
    : `Lines ${lines.join(', ')} have no spoken text. Write them or remove the lines.`;
}
